<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
 
  columns: { type: Array, required: true },
  rows: { type: Array, required: true },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(["submit", "cancel"]);


const draft = ref([]);


const current = ref(0);

watch(
  [() => props.rows, () => props.columns],
  ([rows]) => {
    draft.value = rows.map((row) => ({
      index: row.index,
      values: props.columns.reduce((acc, col) => {
        acc[col.key] = row.values?.[col.key] ?? "";
        return acc;
      }, {}),
    }));
    current.value = 0;
  },
  { immediate: true, deep: false },
);

const record = computed(() => draft.value[current.value]);

function listOptions(col) {
  const list = (col.validations || []).find((v) => v.validate === "list");
  if (!list?.options?.length) return null;

  const seen = new Set();
  return list.options
    .map((o) => String(o))
    .filter((o) => {
      const key = o.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function allowedValues(col) {
  const options = listOptions(col);
  return options ? options.map((o) => o.toLowerCase()) : null;
}

function fieldError(col, value) {
  const val = String(value ?? "").trim();

  if (col.required && val === "") {
    return `${label(col)} is required`;
  }

  const allowed = allowedValues(col);
  if (allowed && val !== "" && !allowed.includes(val.toLowerCase())) {
    return `Must be one of: ${allowed.join(", ")}`;
  }

  return null;
}


function label(col) {
  return String(col.name || col.key).replace(/\s*\(\*\)\s*$/, "");
}

const errorsByRow = computed(() =>
  draft.value.map((row) => {
    const errs = {};
    props.columns.forEach((col) => {
      const err = fieldError(col, row.values[col.key]);
      if (err) errs[col.key] = err;
    });
    return errs;
  }),
);

const currentErrors = computed(() => errorsByRow.value[current.value] || {});

const badRecordNumbers = computed(() =>
  errorsByRow.value.reduce((acc, errs, i) => {
    if (Object.keys(errs).length) acc.push(i + 1);
    return acc;
  }, []),
);

const errorCount = computed(() =>
  errorsByRow.value.reduce((n, errs) => n + Object.keys(errs).length, 0),
);


function summary(row) {
  const po = row.values.po_number;
  if (po) return po;
  const first = props.columns.map((c) => row.values[c.key]).find((v) => String(v ?? "").trim() !== "");
  return first || "(empty)";
}

function go(delta) {
  const next = current.value + delta;
  if (next >= 0 && next < draft.value.length) current.value = next;
}

function jumpToFirstError() {
  const i = errorsByRow.value.findIndex((errs) => Object.keys(errs).length);
  if (i !== -1) current.value = i;
}

function submit() {
  emit("submit", draft.value.map((row) => ({ index: row.index, values: { ...row.values } })));
}
</script>

<template>
  <v-card flat class="review">
    <div class="review-head">
      <div>
        <div class="text-h6">Review before importing</div>
        <div class="text-body-2 text-medium-emphasis">
          {{ draft.length }} {{ draft.length === 1 ? "record" : "records" }} from your file.
          Correct anything that looks wrong — nothing is imported until you press Import.
        </div>
      </div>

      <v-chip v-if="errorCount === 0" color="success" variant="tonal" prepend-icon="mdi-check-circle-outline">
        Ready to import
      </v-chip>
      <v-chip
        v-else
        color="error"
        variant="tonal"
        prepend-icon="mdi-alert-circle-outline"
        class="clickable"
        @click="jumpToFirstError"
      >
        {{ errorCount }} {{ errorCount === 1 ? "problem" : "problems" }} in
        {{ badRecordNumbers.length }} {{ badRecordNumbers.length === 1 ? "record" : "records" }}
      </v-chip>
    </div>

    <v-divider />

    <div class="review-body">
      <!-- Record list: which record is open, and which ones still need work. -->
      <nav class="record-list" aria-label="Records">
        <button
          v-for="(row, i) in draft"
          :key="i"
          type="button"
          class="record-link"
          :class="{ active: i === current, bad: badRecordNumbers.includes(i + 1) }"
          @click="current = i"
        >
          <span class="record-no">{{ i + 1 }}</span>
          <span class="record-name">{{ summary(row) }}</span>
          <v-icon v-if="badRecordNumbers.includes(i + 1)" icon="mdi-alert-circle" size="16" color="error" />
        </button>
      </nav>

      <!-- One record, fields stacked vertically. -->
      <section v-if="record" class="record-detail">
        <header class="record-head">
          <div class="text-subtitle-1 font-weight-medium">
            Record {{ current + 1 }} of {{ draft.length }}
          </div>
          <div class="record-nav">
            <v-btn size="small" variant="text" :disabled="current === 0" @click="go(-1)">Previous</v-btn>
            <v-btn size="small" variant="text" :disabled="current === draft.length - 1" @click="go(1)">Next</v-btn>
          </div>
        </header>

        <dl class="fields">
          <template v-for="col in columns" :key="col.key">
            <dt :class="{ 'is-required': col.required }">
              {{ label(col) }}<span v-if="col.required" aria-hidden="true"> *</span>
            </dt>
            <dd>
              <v-combobox
                v-if="listOptions(col)"
                v-model="record.values[col.key]"
                :items="listOptions(col)"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :error="!!currentErrors[col.key]"
                :error-messages="currentErrors[col.key] ? [currentErrors[col.key]] : []"
              />
              <v-text-field
                v-else
                v-model="record.values[col.key]"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :error="!!currentErrors[col.key]"
                :error-messages="currentErrors[col.key] ? [currentErrors[col.key]] : []"
                :placeholder="col.description || ''"
              />
            </dd>
          </template>
        </dl>
      </section>
    </div>

    <v-divider />

    <div class="review-foot">
      <v-btn variant="text" :disabled="submitting" @click="emit('cancel')">Back to mapping</v-btn>
      <v-btn
        color="#7a5ef8"
        :loading="submitting"
        :disabled="errorCount > 0 || draft.length === 0"
        @click="submit"
      >
        Import {{ draft.length }} {{ draft.length === 1 ? "record" : "records" }}
      </v-btn>
    </div>
  </v-card>
</template>

<style scoped>
.review {
  border: 1px solid rgb(var(--v-border-color), 0.18);
  border-radius: 6px;
}

.review-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1rem 1.25rem;
}

.clickable {
  cursor: pointer;
}

.review-body {
  display: grid;
  grid-template-columns: 15rem minmax(0, 1fr);
  align-items: start;
  min-height: 22rem;
}

.record-list {
  display: flex;
  flex-direction: column;
  max-height: 34rem;
  overflow-y: auto;
  border-right: 1px solid rgb(var(--v-border-color), 0.18);
  padding: 0.5rem;
  gap: 2px;
}

.record-link {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.55rem;
  border-radius: 4px;
  text-align: left;
  font-size: 0.875rem;
  cursor: pointer;
}

.record-link:hover {
  background: rgb(var(--v-theme-on-surface), 0.06);
}

.record-link.active {
  background: rgba(122, 94, 248, 0.14);
  font-weight: 600;
}

.record-link.bad .record-name {
  color: rgb(var(--v-theme-error));
}

.record-no {
  flex: none;
  min-width: 1.5rem;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-on-surface), 0.55);
}

.record-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-detail {
  padding: 1rem 1.25rem 1.5rem;
  min-width: 0;
}

.record-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.record-nav {
  display: flex;
  gap: 0.25rem;
}

/* The vertical layout: label left, value right, one field per line. */
.fields {
  display: grid;
  grid-template-columns: 12rem minmax(0, 1fr);
  align-items: start;
  gap: 0.6rem 1rem;
  margin: 0;
  max-width: 46rem;
}

.fields dt {
  padding-top: 0.5rem;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface), 0.75);
}

.fields dt.is-required {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
}

.fields dd {
  margin: 0;
  min-width: 0;
}

.review-foot {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
}

@media (max-width: 900px) {
  .review-body {
    grid-template-columns: minmax(0, 1fr);
  }

  .record-list {
    flex-direction: row;
    max-height: none;
    overflow-x: auto;
    border-right: 0;
    border-bottom: 1px solid rgb(var(--v-border-color), 0.18);
  }

  .record-link {
    flex: none;
  }

  .record-name {
    max-width: 8rem;
  }

  .fields {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.15rem;
  }

  .fields dt {
    padding-top: 0.5rem;
  }
}
</style>
